/**
 * Node types for the Needs Map
 */

/**
 * Status of a node or edge
 */
export type Status = 'pending' | 'processing' | 'success' | 'error' | 'partial';

/**
 * Error information
 */
export interface ErrorInfo {
  message: string;
  code: string;
}

/**
 * Content item with timestamp
 */
export interface ContentItem {
  content: string;
  timestamp: string; // Format: 'HH:MM:SS'
}

/**
 * Topic node representing a specific theme or experience
 */
export interface TopicNode {
  id: string;
  title: string;
  phaseId: string;
  defaultImportance: number;
  extractionPrompt: string;
  contents: ContentItem[]; // Array of content items with timestamps
  content?: string; // Simplified content string for easier handling
  importance: number;
  status: Status;
  error: ErrorInfo | null;
  active: boolean; // Whether this node is active (has content)
  position?: {
    x: number;
    y: number;
  };
  width?: number;
  height?: number;
}

/**
 * Node data for ReactFlow
 */
export interface NodeData {
  title: string;
  phaseId: string;
  contents: ContentItem[];
  content?: string; // Simplified content string
  importance: number;
  status: Status;
  error: ErrorInfo | null;
  active: boolean;
  extractionPrompt: string;
  defaultImportance: number;
  width?: number;
  height?: number;
}

/**
 * Create a new topic node
 */
export function createTopicNode(
  id: string,
  title: string,
  phaseId: string,
  extractionPrompt: string,
  defaultImportance: number = 3
): TopicNode {
  return {
    id,
    title,
    phaseId,
    defaultImportance,
    extractionPrompt,
    contents: [],
    content: '',
    importance: defaultImportance,
    status: 'pending',
    error: null,
    active: false
  };
}

/**
 * Get the status variant for UI components
 */
export function getStatusVariant(status: Status): 'default' | 'secondary' | 'destructive' | 'success' | 'warning' {
  switch (status) {
    case 'success':
      return 'success';
    case 'error':
      return 'destructive';
    case 'partial':
      return 'warning';
    case 'processing':
      return 'secondary';
    default:
      return 'default';
  }
}

/**
 * Get the status label for display
 */
export function getStatusLabel(status: Status): string {
  switch (status) {
    case 'success':
      return '完了';
    case 'error':
      return 'エラー';
    case 'partial':
      return '部分的';
    case 'processing':
      return '処理中';
    default:
      return '未処理';
  }
}

/**
 * Check if node is active based on contents
 */
export function isNodeActive(node: TopicNode): boolean {
  // A node is active if it has at least one valid content item
  if (node.contents.length > 0) {
    return node.contents.some(item => 
      item.content && 
      item.content.trim() !== '' &&
      !item.content.includes('言及なし') &&
      !item.content.includes('抽出できませんでした')
    );
  } else if (node.content) {
    // Backward compatibility with content string
    return node.content.trim() !== '' &&
           !node.content.includes('言及なし') &&
           !node.content.includes('抽出できませんでした');
  }
  return false;
}

/**
 * Update node active status based on contents
 */
export function updateNodeActiveStatus(node: TopicNode): TopicNode {
  return {
    ...node,
    active: isNodeActive(node)
  };
}